import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Activity, Shield, Clock, User, ChevronLeft, ChevronRight,
  Search, Download
} from 'lucide-react';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/exportUtils';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtTimestamp = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtFull = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ACTION_ICON = {
  Login: '🔐', Logout: '🚪', LoginFailed: '🚫', LoginBlocked: '🔒', OtpFailed: '⛔',
  Create: '➕', Update: '✏️', Delete: '🗑️', View: '👁️',
  Export: '📤', Import: '📥', BulkUpload: '📁',
  AssignAdmin: '👑', BulkUpdate: '🔄', StatusChange: '🔀',
  Register: '📝', PasswordReset: '🔑', ForgotPassword: '💡', System: '⚙️',
};

const ACTION_COLOR_DARK = {
  Login: '#34D399', Logout: '#94A3B8', LoginFailed: '#F87171', LoginBlocked: '#F87171',
  Create: '#60A5FA', Update: '#FBBF24', Delete: '#F87171', BulkUpload: '#A78BFA',
  AssignAdmin: '#FBBF24', Export: '#22D3EE', Import: '#60A5FA',
  StatusChange: '#FB923C', Default: '#94A3B8'
};
const ACTION_COLOR_LIGHT = {
  Login: '#10B981', Logout: '#94A3B8', LoginFailed: '#EF4444', LoginBlocked: '#EF4444',
  Create: '#2563EB', Update: '#D97706', Delete: '#EF4444', BulkUpload: '#7C3AED',
  AssignAdmin: '#D97706', Export: '#06B6D4', Import: '#2563EB',
  StatusChange: '#EA580C', Default: '#94A3B8'
};

const OPERATIONS_ACTIONS = ['Create', 'BulkUpload', 'Import', 'Export', 'AssignAdmin', 'Delete', 'StatusChange', 'BulkUpdate'];
const PER_PAGE = 15;

const SectionCard = ({ title, subtitle, children, isDark, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay }}
    className={`rounded-2xl border ${isDark ? 'bg-[#002147] border-[#1a3884]/50 shadow-lg' : 'bg-white border-gray-200 shadow-sm'}`}
  >
    {(title || subtitle) && (
      <div className="px-6 pt-5 pb-0">
        {title && <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h3>}
        {subtitle && <p className={`text-xs mt-1 mb-5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{subtitle}</p>}
      </div>
    )}
    {children}
  </motion.div>
);

// ─── Activity Feed Item ────────────────────────────────────────────────────────
const FeedItem = ({ log, isDark, index }) => {
  const actionColors = isDark ? ACTION_COLOR_DARK : ACTION_COLOR_LIGHT;
  const color = actionColors[log.action] || actionColors.Default;
  const icon = ACTION_ICON[log.action] || '⚡';
  const userName = log.user?.fullName || log.user?.name || log.userName || '—';
  const userRole = log.user?.role || log.userRole || '';
  const description = log.details || `${log.action} on ${log.module}`;

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.025 }}
      className={`flex items-start gap-3 px-5 py-3.5 border-b last:border-b-0 transition-colors ${
        isDark ? 'border-[#1a3884]/20 hover:bg-[#0F225C]/30' : 'border-gray-100 hover:bg-gray-50'
      }`}
    >
      <span className="text-base flex-shrink-0 mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 justify-between flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold" style={{ color }}>{log.action}</span>
            {log.module && (
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${
                isDark ? 'bg-[#1a3884]/40 text-blue-300' : 'bg-blue-50 text-blue-700'
              }`}>{log.module}</span>
            )}
          </div>
          <span className={`text-[10px] flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {fmtTimestamp(log.createdAt)}
          </span>
        </div>
        {description && (
          <p className={`text-xs mt-0.5 truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{description}</p>
        )}
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {userName !== '—' && (
            <span className={`flex items-center gap-1 text-[10px] font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <User size={9} />
              {userName}
              {userRole && <span className={`ml-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>({userRole})</span>}
            </span>
          )}
          {log.ipAddress && log.ipAddress !== 'Unknown' && (
            <span className={`text-[10px] font-mono ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{log.ipAddress}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ─── Audit Table Row ───────────────────────────────────────────────────────────
const AuditRow = ({ log, isDark }) => {
  const actionColors = isDark ? ACTION_COLOR_DARK : ACTION_COLOR_LIGHT;
  const color = actionColors[log.action] || actionColors.Default;
  const userName = log.user?.fullName || log.user?.name || log.userName || '—';
  const userRole = log.user?.role || log.userRole || '';
  const description = log.details || `${log.action} on ${log.module}`;

  return (
    <tr className={`transition-colors ${isDark ? 'hover:bg-[#0F225C]/30' : 'hover:bg-gray-50'}`}>
      <td className={`px-4 py-2.5 text-xs whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <div className="flex items-center gap-1">
          <Clock size={10} />
          {fmtFull(log.createdAt)}
        </div>
      </td>
      <td className={`px-4 py-2.5 text-xs font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        {log.ipAddress && log.ipAddress !== 'Unknown' ? log.ipAddress : '—'}
      </td>
      <td className="px-4 py-2.5">
        <p className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{userName}</p>
        {userRole && <p className={`text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{userRole}</p>}
      </td>
      <td className="px-4 py-2.5">
        <span className="text-xs font-bold" style={{ color }}>{log.action || '—'}</span>
      </td>
      <td className={`px-4 py-2.5 text-xs max-w-[260px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        <p className="truncate">{description || '—'}</p>
      </td>
    </tr>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const ActivityStream = ({ isDark }) => {
  const [recentActivity, setRecentActivity] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [opLogs, setOpLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);

  // Audit log state
  const [auditSearch, setAuditSearch] = useState('');
  const [auditPage, setAuditPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('all');

  // Fetch recent activity feed
  const fetchActivity = useCallback(async () => {
    setLoadingActivity(true);
    try {
      const res = await api.get('/analytics/recent-activity');
      if (res.data.success) setRecentActivity(res.data.data || []);
    } catch {
      try {
        const res = await api.get('/logs?limit=20');
        if (res.data.success) setRecentActivity(res.data.data || []);
      } catch (e2) {
        console.error('Activity fetch failed:', e2);
      }
    } finally {
      setLoadingActivity(false);
    }
  }, []);

  // Fetch full logs (for audit table + operations timeline)
  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await api.get('/logs?limit=200');
      const data = res.data.data || [];
      setAllLogs(data);
      setOpLogs(data.filter(l => OPERATIONS_ACTIONS.includes(l.action)));
    } catch {
      try {
        const res = await api.get('/analytics/activity-log');
        const data = res.data.data || [];
        setAllLogs(data);
        setOpLogs(data.filter(l => OPERATIONS_ACTIONS.includes(l.action)));
      } catch (e2) {
        console.error('Logs fetch failed:', e2);
      }
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => { fetchActivity(); fetchLogs(); }, [fetchActivity, fetchLogs]);

  // Filtered audit logs
  const filteredLogs = useMemo(() => {
    return allLogs.filter(l => {
      if (actionFilter !== 'all' && l.action !== actionFilter) return false;
      if (auditSearch.trim()) {
        const q = auditSearch.toLowerCase();
        const name = (l.user?.fullName || l.user?.name || l.userName || '').toLowerCase();
        const action = (l.action || '').toLowerCase();
        const details = (l.details || '').toLowerCase();
        const ip = (l.ipAddress || '').toLowerCase();
        if (!name.includes(q) && !action.includes(q) && !details.includes(q) && !ip.includes(q)) return false;
      }
      return true;
    });
  }, [allLogs, actionFilter, auditSearch]);

  const auditTotalPages = Math.ceil(filteredLogs.length / PER_PAGE);
  const auditPaged = filteredLogs.slice((auditPage - 1) * PER_PAGE, auditPage * PER_PAGE);
  useEffect(() => setAuditPage(1), [auditSearch, actionFilter]);

  // Export audit logs
  const handleExportAudit = () => {
    const rows = filteredLogs.map(l => ({
      'Timestamp': fmtFull(l.createdAt),
      'IP Address': l.ipAddress || '—',
      'User': l.user?.fullName || l.userName || '—',
      'Role': l.user?.role || l.userRole || '—',
      'Action': l.action || '—',
      'Description': l.details || `${l.action} on ${l.module}`,
    }));
    exportToCSV(rows, Object.keys(rows[0]).map(k => ({ header: k, accessor: k })), 'Audit_Log');
  };

  // Export operations timeline
  const handleExportOps = () => {
    const rows = opLogs.map(l => ({
      'Timestamp': fmtFull(l.createdAt),
      'IP Address': l.ipAddress || '—',
      'User': l.user?.fullName || l.userName || '—',
      'Action': l.action || '—',
      'Description': l.details || '—',
    }));
    exportToCSV(rows, Object.keys(rows[0]).map(k => ({ header: k, accessor: k })), 'Admin_Operations_Timeline');
  };

  const uniqueActions = useMemo(() => {
    const actions = [...new Set(allLogs.map(l => l.action).filter(Boolean))];
    return ['all', ...actions.sort()];
  }, [allLogs]);

  return (
    <div className="space-y-6">

      {/* ── 1. Admin Operations Timeline ── */}
      <SectionCard
        title="Admin Operations Timeline"
        subtitle="Who created, imported, assigned, uploaded, or deleted what — and when"
        isDark={isDark}
        delay={0}
      >
        <div className={`px-6 pb-4 flex items-center justify-between`}>
          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{opLogs.length} operation records</span>
          <button
            onClick={handleExportOps}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold transition-all ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >
            <Download size={11} /> Export CSV
          </button>
        </div>

        {opLogs.length === 0 ? (
          <div className={`text-center py-12 pb-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No operation logs found</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Operations like Create, Import, BulkUpload, Assign will appear here</p>
          </div>
        ) : (
          <div className="max-h-[400px] overflow-y-auto">
            {opLogs.map((log, i) => {
              const actionColors = isDark ? ACTION_COLOR_DARK : ACTION_COLOR_LIGHT;
              const color = actionColors[log.action] || actionColors.Default;
              const icon = ACTION_ICON[log.action] || '⚡';
              const userName = log.user?.fullName || log.user?.name || log.userName || 'Unknown';
              const description = log.details || `${log.action}${log.module ? ` — ${log.module}` : ''}`;
              return (
                <div key={log._id || i} className={`flex items-start gap-3 px-5 py-3.5 border-b last:border-b-0 transition-colors ${isDark ? 'border-[#1a3884]/20 hover:bg-[#0F225C]/20' : 'border-gray-100 hover:bg-gray-50'}`}>
                  <span className="text-base flex-shrink-0">{icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                       <span className={`font-bold text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>{userName}</span>
                      <span className="text-xs font-bold" style={{ color }}>{log.action}</span>
                    </div>
                    <p className={`text-xs mt-0.5 truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{description}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{fmtTimestamp(log.createdAt)}</span>
                      {log.ipAddress && log.ipAddress !== 'Unknown' && (
                        <span className={`text-[10px] font-mono ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{log.ipAddress}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* ── 2. Audit Log Table ── */}
      <SectionCard
        title="Audit Log"
        subtitle="Complete admin action audit trail — Timestamp · IP · User · Action · Description"
        isDark={isDark}
        delay={0.1}
      >
        {/* Controls */}
        <div className="px-6 pb-4 flex flex-wrap items-center gap-3">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border flex-1 min-w-[180px] ${isDark ? 'bg-[#0F225C] border-[#1a3884]/60' : 'bg-gray-50 border-gray-300'}`}>
            <Search size={13} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
            <input
              type="text"
              value={auditSearch}
              onChange={e => setAuditSearch(e.target.value)}
              placeholder="Search user, action, IP…"
              className={`flex-1 text-xs bg-transparent focus:outline-none ${isDark ? 'text-gray-200 placeholder-gray-600' : 'text-gray-700 placeholder-gray-400'}`}
            />
          </div>
          <select
            value={actionFilter}
            onChange={e => setActionFilter(e.target.value)}
            className={`text-xs font-semibold rounded-xl px-3 py-2 border focus:outline-none ${isDark ? 'bg-[#0F225C] border-[#1a3884]/60 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
          >
            {uniqueActions.map(a => <option key={a} value={a}>{a === 'all' ? 'All Actions' : a}</option>)}
          </select>
          <button
            onClick={handleExportAudit}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold transition-all ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >
            <Download size={12} /> CSV
          </button>
          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{filteredLogs.length} records</span>
        </div>

        {loadingLogs ? (
          <div className="flex items-center justify-center h-24 pb-4">
            <div className="relative w-8 h-8">
              <div className={`absolute inset-0 border-4 rounded-full ${isDark ? 'border-[#1a3884]/40' : 'border-gray-200'}`} />
              <div className="absolute inset-0 border-4 border-[#284BB3] rounded-full border-t-transparent animate-spin" />
            </div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className={`text-center py-12 pb-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No audit records found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-t border-b ${isDark ? 'border-[#1a3884]/30 bg-[#0F225C]/30' : 'border-gray-200 bg-gray-50'}`}>
                    {['Timestamp', 'IP Address', 'User', 'Action', 'Description'].map(h => (
                      <th key={h} className={`px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-wide ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-[#1a3884]/20' : 'divide-gray-100'}`}>
                  {auditPaged.map((log, i) => (
                    <AuditRow key={log._id || i} log={log} isDark={isDark} />
                  ))}
                </tbody>
              </table>
            </div>
            {auditTotalPages > 1 && (
              <div className={`flex items-center justify-between px-6 py-4 border-t ${isDark ? 'border-[#1a3884]/30' : 'border-gray-200'}`}>
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Page {auditPage} of {auditTotalPages}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setAuditPage(p => Math.max(1, p - 1))} disabled={auditPage === 1} className={`p-1.5 rounded-lg border transition-all disabled:opacity-40 ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}><ChevronLeft size={14} /></button>
                  <button onClick={() => setAuditPage(p => Math.min(auditTotalPages, p + 1))} disabled={auditPage === auditTotalPages} className={`p-1.5 rounded-lg border transition-all disabled:opacity-40 ${isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}><ChevronRight size={14} /></button>
                </div>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* ── 3. Admin Activity Stream ── */}
      <SectionCard
        title="Admin Activity Stream"
        subtitle="Real-time feed of recent admin actions"
        isDark={isDark}
        delay={0.2}
      >
        {loadingActivity ? (
          <div className="flex items-center justify-center h-32">
            <div className="relative w-10 h-10">
              <div className={`absolute inset-0 border-4 rounded-full ${isDark ? 'border-[#1a3884]/40' : 'border-gray-200'}`} />
              <div className="absolute inset-0 border-4 border-[#284BB3] rounded-full border-t-transparent animate-spin" />
            </div>
          </div>
        ) : recentActivity.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No recent activity</p>
          </div>
        ) : (
          <div className="max-h-[380px] overflow-y-auto">
            {recentActivity.map((log, i) => (
              <FeedItem key={log._id || i} log={log} isDark={isDark} index={i} />
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default ActivityStream;
